import { createReducer, AnyAction } from '@reduxjs/toolkit';

import { capabilities as capabilitiesState } from '../state/capabilities';
// Circular dependency: code-bench.actions.ts imports from the barrel which imports this reducer.
// Use string literals for all action type identifiers from circular imports.
import {
  determineBenchEditable,
  getImprovements,
  getRenderSubmitCase,
  getRenderviewOnlyCase,
  getActionMenuCancelCapability,
  getRenderPagination,
  getRenderHoldCase,
  getRenderMergeableCase,
  getRenderOffHoldCase,
  getRenderToggleChargeableCase,
  getCaseEditable,
  getActionMenuCapability,
  getGeneralCancelCapability,
  getImprovementsAction,
  getOldCaseLoadedAlert,
  getPreviousSentCaseAlert,
  getServicedeskActions,
  getToggleCaseComment,
  getNoteUnavailableReason,
  getMergeActionMenuCapability,
  getCoderFeedbackCapability,
  getCreateCaseCapability,
  getSplitActionMenuCapability,
  getTimelineInfiniteLoadCapability,
  getProductivityTrackerCapability,
  getAttachDocsCapability,
  getDiagnosisCodeSuggestionCapability,
  getCoderNotesCapability,
  getTransactionV2Capability,
  getAmplifyCaseRunV2Capability,
  getActionBarSelectMultipleWorkitemsCapability,
  getGetCaserunWithRefreshCapability,
  getUseProviderDirectoryLocations,
  getCodeSourcesFeaturesCapability,
  getRelatedCasesFilterCapability,
  getLateCaseClearNotificationCapability,
  getUseDataAccessAmplifyCapability
} from '../utils/bench-editable';
import {
  chooseContext,
  saveServiceLineCapability,
  resetCommonState
} from '../../../actions/ui.actions';
import * as state from '../state/index';
import * as commonEnums from '../../../enums/index';
import { splitCase } from '../../../actions/merge-cases.actions';
import * as gradeBenchPanelActions from '../../../actions/grade-bench-panel-actions';
import { isEmpty } from '../../object-utils';

const updateCapabilities = (
  state,
  {
    result: processInstance,
    isCaseAccessReadOnly
  }: {
    result: AmplifyAPI.ProcessInstance;
    isCaseAccessReadOnly?: boolean;
  }
) => {
  const benchEditable = determineBenchEditable(
    processInstance,
    state.userContext,
    state.roles,
    state.parentCaserunUid,
    state.submitInProgress,
    isCaseAccessReadOnly
  );
  const improvements: CMxCommonApp.Capability = getImprovements(
    processInstance,
    benchEditable
  );
  const improvementsAction: CMxCommonApp.Capability = getImprovementsAction(
    processInstance,
    benchEditable
  );
  const submitCase: CMxCommonApp.Capability = getRenderSubmitCase(
    processInstance,
    benchEditable
  );

  const viewOnlyCase: CMxCommonApp.Capability = getRenderviewOnlyCase(
    benchEditable
  );
  const pagination: CMxCommonApp.Capability = getRenderPagination(
    processInstance,
    benchEditable
  );
  const holdCase: CMxCommonApp.Capability = getRenderHoldCase(
    processInstance,
    state.holdCaseStatus,
    benchEditable
  );
  const removeHoldCase: CMxCommonApp.Capability = getRenderOffHoldCase(
    processInstance,
    state.holdCaseStatus,
    benchEditable
  );
  const toggleChargeableCase: CMxCommonApp.Capability = getRenderToggleChargeableCase(
    processInstance,
    benchEditable,
    state.roles,
    state.excludeCasePermissionsConfig
  );

  const caseEdit: CMxCommonApp.Capability = getCaseEditable(
    processInstance,
    benchEditable
  );

  const createCase = getCreateCaseCapability(
    state.userContext,
    processInstance
  );

  const splitCapability = getSplitActionMenuCapability(
    benchEditable,
    state.userContext,
    state.parentCase,
    state.activeServiceLineName
  );

  const lateCaseClearNotification = getLateCaseClearNotificationCapability(
    processInstance
  );

  // This has dependency on holdCase, toggleChargeableCase, splitCapability from above
  const actionMenu: CMxCommonApp.Capability = getActionMenuCapability(
    holdCase,
    removeHoldCase,
    toggleChargeableCase,
    splitCapability,
    lateCaseClearNotification,
    processInstance
  );

  const diagnosisCodeSuggestion: CMxCommonApp.Capability = getDiagnosisCodeSuggestionCapability(
    state.userContext,
    processInstance,
    benchEditable
  );

  const coderNotes: CMxCommonApp.Capability = getCoderNotesCapability(
    state.userContext,
    state.activeServiceLineName
  );

  const transactionV2: CMxCommonApp.Capability = getTransactionV2Capability(
    state.userContext,
    state.activeServiceLineName
  );

  const amplifyCaseRunV2: CMxCommonApp.Capability = getAmplifyCaseRunV2Capability(
    state.userContext,
    state.activeServiceLineName
  );

  const actionMenuCancelButton: CMxCommonApp.Capability = getActionMenuCancelCapability(
    processInstance,
    benchEditable,
    actionMenu,
    state.roles,
    state.userContext?.resources
  );

  // This has dependency on cancelCase and toggleChargeableCase from above
  const generalCancelButton: CMxCommonApp.Capability = getGeneralCancelCapability(
    actionMenu,
    actionMenuCancelButton,
    benchEditable,
    state.userContext?.resources,
    state.activeServiceLineName
  );

  const servicedeskAction: CMxCommonApp.Capability = getServicedeskActions(
    processInstance,
    state.cursorAPIContext
  );

  const mergeCapability = getMergeActionMenuCapability(
    benchEditable,
    state.userContext,
    state.parentCase,
    state.activeItem,
    state.activeServiceLineName
  );

  const coderFeedback = getCoderFeedbackCapability(
    benchEditable,
    processInstance,
    state.userContext
  );

  const actionBarSelectMultipleWorkitems = getActionBarSelectMultipleWorkitemsCapability(
    state.roles
  );

  const codeSourcesFeatures = getCodeSourcesFeaturesCapability(
    state.userContext,
    state.activeServiceLineName
  );

  const relatedCasesFilter: CMxCommonApp.Capability = getRelatedCasesFilterCapability(
    state.userContext,
    state.activeServiceLineName
  );

  const useDataAccessAmplify: CMxCommonApp.Capability = getUseDataAccessAmplifyCapability(
    state.userContext,
    state.activeServiceLineName
  );

  const capabilities = {
    ...state.capabilities,
    improvements,
    submitCase,
    actionMenuCancelButton,
    viewOnlyCase,
    pagination,
    holdCase,
    removeHoldCase,
    toggleChargeableCase,
    caseEdit,
    actionMenu,
    generalCancelButton,
    improvementsAction,
    servicedeskAction,
    mergeCapability,
    splitCapability,
    coderFeedback,
    createCase,
    diagnosisCodeSuggestion,
    coderNotes,
    transactionV2,
    amplifyCaseRunV2,
    actionBarSelectMultipleWorkitems,
    codeSourcesFeatures,
    relatedCasesFilter,
    lateCaseClearNotification,
    useDataAccessAmplify
  };

  return capabilities;
};

const updateCapabilitiesOnContext = (
  activeContext: any,
  state: any,
  activeServiceLineName: string = ''
) => {
  const timelineInfiniteLoad = getTimelineInfiniteLoadCapability(
    activeContext,
    activeServiceLineName
  );

  const attachDocs = getAttachDocsCapability(
    activeContext,
    activeServiceLineName
  );

  const productivityTracker = getProductivityTrackerCapability(
    activeContext,
    activeServiceLineName
  );

  const getCaseRunWithRefresh: CMxCommonApp.Capability = getGetCaserunWithRefreshCapability(
    activeContext,
    activeServiceLineName
  );

  const useProviderDirectoryLocations: CMxCommonApp.Capability = getUseProviderDirectoryLocations(
    activeContext,
    activeServiceLineName
  );

  const capabilities = {
    ...state.capabilities,
    timelineInfiniteLoad,
    attachDocs,
    productivityTracker,
    getCaseRunWithRefresh,
    useProviderDirectoryLocations
  };

  return capabilities;
};

const getConcurrentCaseAccessCapabilities = (
  state: any,
  payload: any,
  isCaseAccessReadOnly: boolean
) => {
  // Environment variable: ALLOW_CONCURRENT_CASE_EDIT
  // If set to "true" (or not defined), capabilities remain unchanged.
  // If set to "false", capabilities such as action menu, case edit, etc. will be removed.
  const isConcurrentCaseEditAllowed =
    process.env.ALLOW_CONCURRENT_CASE_EDIT !== 'false'; // default true if not set

  if (isConcurrentCaseEditAllowed) {
    return state.capabilities;
  }

  return updateCapabilities(state, {
    result: payload,
    isCaseAccessReadOnly
  });
};

const capabilities = createReducer(capabilitiesState, builder => {
  builder.addCase(resetCommonState.type, () => ({ ...capabilitiesState }));

  builder.addCase('FETCH_EXCLUDE_CASE_PERMISSIONS_CONFIG/fulfilled', (state, action: AnyAction) => {
    return {
      ...state,
      excludeCasePermissionsConfig: action.payload
    };
  });

  builder.addCase('LOAD_ROLES', (state, action: AnyAction) => {
    return {
      ...state,
      roles: action.payload
    };
  });

  builder.addCase('CASE_DETAIL', (state, action: AnyAction) => {
    const activeItem = action.payload;
    const noteUnavailableReason = getNoteUnavailableReason(activeItem);
    return {
      ...state,
      parentCaserunUid: activeItem?.parent_caserun_uid,
      parentCase: activeItem?.parent_case,
      activeItem: activeItem,
      capabilities: {
        ...state.capabilities,
        noteUnavailableReason
      }
    };
  });

  builder.addCase('LOAD_CURSOR_API_CONTEXT', (state, action: AnyAction) => {
    const payload = (action.payload ?? {}) as Partial<CMxCommonApp.CursorAPIContext>;
    const cursorAPIContext = {
      ...state.cursorAPIContext,
      ...payload
    } as CMxCommonApp.CursorAPIContext;
    const servicedeskAction = getServicedeskActions(
      state.activeItem as unknown as AmplifyAPI.ProcessInstance,
      cursorAPIContext
    );
    return {
      ...state,
      cursorAPIContext,
      capabilities: {
        ...state.capabilities,
        servicedeskAction
      }
    };
  });

  builder.addCase('RESET_CURSOR_API_CONTEXT', state => {
    const cursorAPIContext = {
      ...capabilitiesState.cursorAPIContext
    } as CMxCommonApp.CursorAPIContext;
    const servicedeskAction = getServicedeskActions(
      state.activeItem as unknown as AmplifyAPI.ProcessInstance,
      cursorAPIContext
    );
    return {
      ...state,
      cursorAPIContext,
      capabilities: {
        ...state.capabilities,
        servicedeskAction
      }
    };
  });

  builder.addCase('SHOW_CASE_COMMENT_MODAL', state => {
    const toggleCaseComment = getToggleCaseComment(
      state.capabilities.removeHoldCase,
      true
    );
    const capabilities = {
      ...state.capabilities,
      toggleCaseComment
    };
    return {
      ...state,
      capabilities
    };
  });

  builder.addCase('HIDE_CASE_COMMENT_MODAL', state => {
    const toggleCaseComment = getToggleCaseComment(
      state.capabilities.removeHoldCase,
      false
    );
    const capabilities = {
      ...state.capabilities,
      toggleCaseComment
    };
    return {
      ...state,
      capabilities
    };
  });

  builder.addCase('USER_DETAILS', (state, action: AnyAction) => {
    const userDetails = action.payload;
    const userContext = userDetails.contexts.find(
      context => context.organizationId === userDetails.activeContextId
    );

    return {
      ...state,
      userContext,
      capabilities: updateCapabilitiesOnContext(
        userContext,
        state,
        state.activeServiceLineName
      )
    };
  });

  builder.addCase('FETCH_HOLD_CASE/fulfilled', (state, action: AnyAction) => {
    const processInstance = action.meta.arg;
    const holdCaseStatus = action.payload;
    const benchEditable = determineBenchEditable(
      processInstance,
      state.userContext,
      state.roles,
      state.parentCaserunUid,
      state.submitInProgress
    );
    const holdCaseCapability: CMxCommonApp.Capability = getRenderHoldCase(
      processInstance,
      holdCaseStatus,
      benchEditable
    );
    const removeHoldCase: CMxCommonApp.Capability = getRenderOffHoldCase(
      processInstance,
      holdCaseStatus,
      benchEditable
    );
    const capabilities = {
      ...state.capabilities,
      holdCase: holdCaseCapability,
      removeHoldCase
    };

    return {
      ...state,
      capabilities,
      holdCaseStatus
    };
  });

  builder.addCase('FETCH_CASE_VERSIONS/fulfilled', (state, action: AnyAction) => {
    const processInstance = action.meta.arg;
    const caseVersions = action.payload;

    const oldCaseLoadedAlert: CMxCommonApp.Capability = getOldCaseLoadedAlert(
      caseVersions,
      processInstance
    );

    const previousSentCaseAlert: CMxCommonApp.Capability = getPreviousSentCaseAlert(
      caseVersions,
      processInstance
    );

    const capabilities = {
      ...state.capabilities,
      oldCaseLoadedAlert,
      previousSentCaseAlert
    };

    return {
      ...state,
      capabilities
    };
  });

  builder.addCase('INVOICE_AVAILABLE/fulfilled', (state, action: AnyAction) => {
    const processInstance = action.payload;
    const capabilities = updateCapabilities(state, { result: processInstance });
    return {
      ...state,
      capabilities
    };
  });

  builder.addCase('CANCEL_INVOICES_CONFIRM/fulfilled', (state, action: AnyAction) => {
    const processInstance = action.payload;
    const capabilities = updateCapabilities(state, { result: processInstance });
    return {
      ...state,
      capabilities
    };
  });

  builder.addCase('TOGGLE_CHARGEABLE_CASE/fulfilled', (state, action: AnyAction) => {
    const processInstance = action.payload;
    const capabilities = updateCapabilities(state, { result: processInstance });
    return {
      ...state,
      capabilities
    };
  });

  builder.addCase('SPLIT_CASE/fulfilled', (state, action: AnyAction) => {
    const processInstance = action.payload;
    const capabilities = updateCapabilities(state, { result: processInstance });
    return {
      ...state,
      capabilities
    };
  });

  builder.addCase(chooseContext.type, (state, action: AnyAction) => {
    const { activeContext } = action.payload;
    const capabilities = updateCapabilitiesOnContext(
      activeContext,
      state,
      state.activeServiceLineName
    );
    return {
      ...state,
      capabilities,
      userContext: activeContext
    };
  });

  builder.addCase('LOAD_CQA_WORK_ITEM_FROM_URL/fulfilled', state => {
    const actionBarSelectMultipleWorkitems = getActionBarSelectMultipleWorkitemsCapability(
      state.roles
    );
    return {
      ...state,
      capabilities: {
        ...state.capabilities,
        actionBarSelectMultipleWorkitems
      }
    };
  });

  builder.addCase('INVOICE_SUBMITTED/pending', (state, action: AnyAction) => {
    const { processInstance } = action.meta.arg;
    const submitInProgress = true;
    const capabilities = updateCapabilities(
      {
        ...state,
        submitInProgress
      },
      {
        result: processInstance
      }
    );
    return {
      ...state,
      capabilities,
      submitInProgress
    };
  });

  builder.addCase('INVOICE_SUBMITTED/rejected', (state, action: AnyAction) => {
    const { processInstance } = action.meta.arg;
    const submitInProgress = false;
    const capabilities = updateCapabilities(
      {
        ...state,
        submitInProgress
      },
      {
        result: processInstance
      }
    );
    return {
      ...state,
      capabilities,
      submitInProgress
    };
  });

  // Merged handler: sets submitInProgress = false AND updates capabilities
  builder.addCase('INVOICE_SUBMITTED/fulfilled', (state, action: AnyAction) => {
    const submitInProgress = false;
    const processInstance = action.payload;
    const capabilities = updateCapabilities(
      {
        ...state,
        submitInProgress
      },
      {
        result: processInstance
      }
    );
    return {
      ...state,
      capabilities,
      submitInProgress
    };
  });

  builder.addCase(saveServiceLineCapability.type, (state, action: AnyAction) => {
    const activeServiceLineName = action.payload;
    const activeSL = isEmpty(activeServiceLineName)
      ? state.activeServiceLineName
      : activeServiceLineName;
    const capabilities = updateCapabilitiesOnContext(
      state.userContext,
      state,
      activeSL
    );
    return {
      ...state,
      activeServiceLineName: activeSL,
      capabilities
    };
  });

  builder.addCase('CASE_OPEN_SUCCESS', (state, action: AnyAction) => {
    return {
      ...state,
      capabilities: getConcurrentCaseAccessCapabilities(state, action.payload, false)
    };
  });

  builder.addCase('CASE_OPEN_CONFLICT', (state, action: AnyAction) => {
    return {
      ...state,
      capabilities: getConcurrentCaseAccessCapabilities(state, action.payload, true)
    };
  });

  builder.addCase('RESET_CASE_ACCESS', (state, action: AnyAction) => {
    return {
      ...state,
      capabilities: getConcurrentCaseAccessCapabilities(state, action.payload, false)
    };
  });
});

export { capabilities };
